import classnames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Size } from '@sorrem/shared';
import type { UISkillTreeView } from '@sorrem-client/types';
import { useValueStoreEffect } from '@sorrem-client/ui/hooks';
import { SKILL_TREE_MIN_ZOOM, type SkillTreeViewStore } from './skill-tree-view';
import { usePrefersReducedMotion } from '@sorrem-client/ui/hooks';
import { createSoftDotSprite, fitCanvasToViewport, type SoftDotStop } from './skill-tree-canvas';
import { useCanvasFrameLoop } from './use-canvas-frame-loop';
import { createVeilSkyMotes, forEachVeilSkyMote, layoutVeilSkyImage, type VeilSkyMoteLayer } from './veil-sky';
import styles from './SkillTreeBackground.module.scss';

/** The painted Veil sky, served from `game-content/assets` (see `assets-router.ts`). */
const SKY_IMAGE_URL = '/assets/skill-tree/backdrop/veil-sky.jpg';
/** Resolution of each layer's pre-drawn soft dot, in canvas pixels. */
const MOTE_SPRITE_SIZE = 32;
/** Each mote's falloff: solid at the center, a soft glow, gone at the edge. */
const MOTE_SPRITE_STOPS: readonly SoftDotStop[] = [
    { offset: 0, alpha: 1 },
    { offset: 0.35, alpha: 0.6 },
    { offset: 1, alpha: 0 },
];

interface SkillTreeBackgroundProps {
    /** Seeds the mote field; pass the character id so each character's sky is its own. */
    readonly seed: string;
    /** The tree's pan/zoom; the sky and motes follow it with parallax, written straight to the page. */
    readonly viewStore: SkillTreeViewStore;
    /** "Character Home effects" Minimal or All: when off, the motes hold still. */
    readonly animationsEnabled: boolean;
    /** Fades the sky in or out. A hidden sky stops animating but stays loaded, so fading back in is instant. */
    readonly isVisible: boolean;
    readonly className?: string;
    /** Called once the painting has decoded (or failed to), so the tree is never shown over an empty sky. */
    readonly onReady?: () => void;
}

function drawMotes(
    canvas: HTMLCanvasElement,
    layers: readonly VeilSkyMoteLayer[],
    sprites: readonly HTMLCanvasElement[],
    view: UISkillTreeView,
    viewport: Size,
    seconds: number,
): void {
    const fitted = fitCanvasToViewport(canvas, viewport);
    if (!fitted) return;
    const { context, pixelRatio } = fitted;
    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    context.clearRect(0, 0, viewport.width, viewport.height);
    // Motes add their light to each other, like the sky's glow.
    context.globalCompositeOperation = 'lighter';
    forEachVeilSkyMote(layers, { ...view, minZoom: SKILL_TREE_MIN_ZOOM }, viewport, seconds, (layerIndex, position, size) => {
        const sprite = sprites[layerIndex];
        if (sprite) context.drawImage(sprite, position.x - size / 2, position.y - size / 2, size, size);
    });
    context.globalCompositeOperation = 'source-over';
}

/**
 * The skill tree's background: the painted Veil sky with a light, seeded
 * field of drifting motes over it, following the tree's pan and zoom with
 * parallax. A page layer (the painting as an image, the motes on a 2D
 * canvas), so it moves in step with the tree and shows as soon as the image
 * decodes. Pointer events pass through to the layers above.
 */
export function SkillTreeBackground({ seed, viewStore, animationsEnabled, isVisible, className, onReady }: SkillTreeBackgroundProps): JSX.Element {
    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [ready, setReady] = useState(false);
    const reducedMotion = usePrefersReducedMotion();
    const animate = animationsEnabled && isVisible && !reducedMotion;
    const layers = useMemo(() => createVeilSkyMotes(seed), [seed]);
    const sprites = useMemo(() => layers.map((layer) => createSoftDotSprite(layer.color, MOTE_SPRITE_SIZE, MOTE_SPRITE_STOPS)), [layers]);
    const secondsRef = useRef(0);
    const onReadyRef = useRef(onReady);
    onReadyRef.current = onReady;

    // The sky covers the layer and the motes fill it.
    // With motion, the motes drift and twinkle every frame, following the latest view.
    const viewport = useCanvasFrameLoop(containerRef, animate, (deltaSeconds, size) => {
        secondsRef.current += deltaSeconds;
        const canvas = canvasRef.current;
        if (canvas) drawMotes(canvas, layers, sprites, viewStore.get(), size, secondsRef.current);
    });

    // Ready once the painting can draw; a failed image still lets the tree show.
    useEffect(() => {
        const image = imageRef.current;
        if (!image) return;
        let cancelled = false;
        const finish = (): void => {
            if (cancelled) return;
            setReady(true);
            onReadyRef.current?.();
        };
        image.decode().then(finish, finish);
        return () => {
            cancelled = true;
        };
    }, []);

    // The painting follows the view; without motion, so does a still frame of the motes.
    const placeSky = useCallback((view: UISkillTreeView): void => {
        const image = imageRef.current;
        if (image) {
            const sky = layoutVeilSkyImage({ ...view, minZoom: SKILL_TREE_MIN_ZOOM }, viewport);
            image.style.width = `${sky.width}px`;
            image.style.height = `${sky.height}px`;
            image.style.transform = `translate(${sky.x}px, ${sky.y}px)`;
        }
        const canvas = canvasRef.current;
        if (canvas && viewport.width > 0 && !animate) {
            drawMotes(canvas, layers, sprites, view, viewport, secondsRef.current);
        }
    }, [animate, layers, sprites, viewport]);
    useValueStoreEffect(viewStore, placeSky);

    return (
        <div
            ref={containerRef}
            className={classnames(styles.skillTreeBackground, ready && isVisible && styles.visible, className)}
            aria-hidden="true"
        >
            <img
                ref={imageRef}
                className={styles.sky}
                src={SKY_IMAGE_URL}
                alt=""
                draggable={false}
            />
            <canvas ref={canvasRef} className={styles.motes} />
        </div>
    );
}
